import i18next from 'i18next';
import { CircleCheckIcon, Trash2Icon } from 'lucide-react';
import React, { useState } from 'react';
import { Control, useFieldArray } from 'react-hook-form';
import { toast } from 'sonner';
import * as z from 'zod';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { createMedia } from '@/queries/useMedia';
import s3api from '@/services/s3_api';

// Maximum allowed video size (95 MB)
const MAX_VIDEO_SIZE = 95 * 1024 * 1024;

export const attachmentsValidation = () => z.array(z.object({ mediaId: z.string(), fileName: z.string() })).optional();

type VideoUploadFieldProps = {
  containerId: string;
  name: string;
  control: Control<any>;
  onUploadSuccess?: () => void;
  onUploadStateChange?: (isUploading: boolean) => void;
  allowedFileCount?: number;
};

type UploadingMap = Record<number, number>; // index -> progress percentage

const VideoUploadFormField: React.FC<VideoUploadFieldProps> = ({
  containerId,
  name,
  control,
  onUploadSuccess,
  onUploadStateChange,
  allowedFileCount = 6
}) => {
  const [uploadProgress, setUploadProgress] = useState<UploadingMap>({});

  const { fields, append, remove, update } = useFieldArray({
    control,
    name,
    shouldUnregister: true
  });

  // Handle file input changes.
  const validateFile = (file: File) => {
    if (!file.type.startsWith('video/') && !file.type.startsWith('image/')) {
      toast.error(i18next.t('validations.video_file'));
      return { valid: false, error: i18next.t('validations.video_file') };
    }
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error(i18next.t('validations.video_file_max_size'));
      return { valid: false, error: i18next.t('validations.video_file_max_size') };
    }

    return { valid: true };
  };

  const setUploadingState = (uploading: boolean) => {
    onUploadStateChange?.(uploading);
  };

  const removeUploadFromProgress = (fieldIndex: number) => {
    setUploadProgress(prev => {
      const { [fieldIndex]: _, ...rest } = prev;
      setUploadingState(Object.keys(rest).length > 0);
      return rest;
    });
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateFile(file);
    if (!validation.valid) {
      // Clear the input
      e.target.value = '';

      return;
    }

    setUploadProgress(prev => ({
      ...prev,
      [index]: 0
    }));
    setUploadingState(true);

    try {
      const {
        data: {
          data: {
            media: { id, presigned_url }
          }
        }
      } = await createMedia({
        file_name: file.name,
        content_type: file.type
      });

      await s3api.put(presigned_url, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: progressEvent => {
          const percent = progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0;
          setUploadProgress(prev => ({
            ...prev,
            [index]: percent
          }));
        }
      });

      // Update the specific field with the uploaded file info
      update(index, { mediaId: id, fileName: file.name });

      onUploadSuccess?.();
    } finally {
      removeUploadFromProgress(index);
    }

    // Allow selecting the same file again by resetting the input value.
    e.target.value = '';
  };

  return (
    <div id={containerId} className="flex flex-col gap-5">
      {fields.map((field, index) => {
        const attachment = field as unknown as { mediaId: string; fileName: string };
        const uploadProgressValue = uploadProgress[index];
        const isUploading = uploadProgressValue !== undefined;

        return (
          <div key={field.id} className="flex gap-2 items-start">
            <FormField
              control={control}
              name={`${name}.${index}`}
              render={() => (
                <FormItem className="flex flex-col flex-1">
                  {index === 0 && <FormLabel>{i18next.t('mission_report.attachments')}</FormLabel>}
                  <FormControl>
                    {!!attachment.mediaId ? (
                      <div className="flex items-center gap-2 rounded border px-3 py-2">
                        <CircleCheckIcon className="h-4 w-4 text-emerald-500" />
                        <span className="break-all text-sm flex-1">{attachment.fileName}</span>
                      </div>
                    ) : isUploading ? (
                      <div className="space-y-2">
                        <Input
                          type="file"
                          accept="video/*,image/*"
                          onChange={e => handleFileChange(e, index)}
                          className="py-2 px-4"
                          disabled
                        />
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <div className="relative flex-1 h-2 bg-muted rounded">
                            <div
                              className="absolute inset-y-0 left-0 bg-blue-500 rounded"
                              style={{ width: `${uploadProgressValue}%` }}
                            />
                          </div>
                          <span>{uploadProgressValue}%</span>
                        </div>
                      </div>
                    ) : (
                      <Input
                        type="file"
                        accept="video/*,image/*"
                        onChange={e => handleFileChange(e, index)}
                        className="py-2 px-4"
                      />
                    )}
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {index > 0 && (
              <Button type="button" size="lg" variant="outline" onClick={() => remove(index)}>
                <Trash2Icon className="" />
              </Button>
            )}
          </div>
        );
      })}
      {fields.length < allowedFileCount && (
        <Button size="lg" type="button" variant="secondary" onClick={() => append({ mediaId: '', fileName: '' })}>
          {i18next.t('controls.add')}
        </Button>
      )}
    </div>
  );
};

export { VideoUploadFormField };
